import type {
  CanvasCard,
  CardType,
  ChartCardConfig,
  ChartType,
  KpiCardConfig,
  SizePreset,
  SummaryCardConfig,
  TableCardConfig
} from "store/types";

export const DEFAULT_MEASURE = "Estimated NAV";
export const DEFAULT_CHARACTERISTIC = "assetClass";
export const EMPTY_CARD_FILTERS = { investors: [], assetClasses: [] };

export function buildChartConfig(): ChartCardConfig {
  return {
    type: "chart",
    chartType: "pie" as ChartType,
    pieDimension: "assetClass",
    graphDataFormation: {
      default: { measure: DEFAULT_MEASURE, groupBy: DEFAULT_CHARACTERISTIC, chartType: "pie" as ChartType }
    },
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

export function buildKpiConfig(): KpiCardConfig {
  return {
    type: "kpi",
    metrics: ["totalNAV", "irr", "moic", "dpi"],
    cubics: { cubic1: { title: DEFAULT_MEASURE, filter: DEFAULT_MEASURE } },
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

export function buildSummaryConfig(): SummaryCardConfig {
  return {
    type: "summary",
    parameter: DEFAULT_MEASURE,
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

export function buildTableConfig(): TableCardConfig {
  return {
    type: "table",
    visibleColumns: ["name", "assetClass", "nav", "irr", "moic", "dpi"],
    columns: [{ aggregation: DEFAULT_MEASURE }],
    groupBy: [[DEFAULT_CHARACTERISTIC], ["deal"]],
    rawGroupBy: [DEFAULT_CHARACTERISTIC, "none"],
    rowBy: "deal",
    measures: [DEFAULT_MEASURE],
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

function uid(): string {
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function card(
  type: CardType,
  widthPercent: number,
  lastPreset: SizePreset | null,
  config: CanvasCard["config"],
  page: number,
  rowIndex = 0,
  orderInRow = 0
): CanvasCard {
  return { id: uid(), type, widthPercent, lastPreset, rowIndex, orderInRow, config, page };
}

export function createDefaultCardMock(type: CardType, page = 0): CanvasCard {
  switch (type) {
    case "chart":
      return card(type, 50, "M", buildChartConfig(), page);
    case "kpi":
      return card(type, 100, "F", buildKpiConfig(), page);
    case "summary":
      return card(type, 50, "M", buildSummaryConfig(), page);
    case "table":
      return card(type, 100, "F", buildTableConfig(), page);
    case "text":
      return card(type, 33.33, "S", { type: "text", content: "" }, page);
    case "ai_card":
      return card(type, 50, "M", { type: "ai_card", question: "", answer: "" }, page);
    case "exposure":
      return card(
        type,
        50,
        "M",
        { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" },
        page
      );
    case "cashflow":
      return card(type, 50, "M", { type: "cashflow" }, page);
    case "unfunded":
      return card(type, 50, "M", { type: "unfunded" }, page);
    case "performance":
      return card(type, 50, "M", { type: "performance", sortBy: "irr", topN: 3 }, page);
    case "benchmark":
      return card(type, 50, "M", { type: "benchmark", benchmarkIds: ["^SPX"] }, page);
    case "dataFreshness":
      return card(type, 33.33, "S", { type: "dataFreshness" }, page);
    case "macroBrief":
      return card(type, 100, "F", { type: "macroBrief", indicators: [] }, page);
  }
}
